import { brandConfig } from "@/lib/brand-config";

interface LogoProps {
    size?: "sm" | "md" | "lg";
    dark?: boolean;
    iconOnly?: boolean;
    className?: string;
}

export function Logo({ size = "md", dark = false, iconOnly = false, className = "" }: LogoProps) {
    const sizes = {
        sm: { width: 120, height: 32, iconSize: 50, fontSize: 16, taglineSize: 10 },
        md: { width: 240, height: 64, iconSize: 80, fontSize: 24, taglineSize: 14 },
        lg: { width: 300, height: 80, iconSize: 100, fontSize: 32, taglineSize: 18 },
    };

    const { iconSize, fontSize, taglineSize, width, height } = sizes[size];
    const primaryColor = dark ? brandConfig.colors.darkMode.primary : brandConfig.colors.haciendaBlue.hex;
    const accentColor = dark ? brandConfig.colors.darkMode.accent : brandConfig.colors.premiumGold.hex;
    const circleOpacity = dark ? brandConfig.colors.darkMode.circleOpacity : 1;

    if (iconOnly) {
        return (
            <svg
                style={{ width: iconSize, height: iconSize }}
                viewBox="0 0 80 80"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className={className}
            >
                {/* Document Brackets */}
                <path
                    d={brandConfig.logo.icon.leftBracket}
                    stroke={primaryColor}
                    strokeWidth={brandConfig.logo.strokeWidths.brackets}
                    strokeLinecap="round"
                />
                <path
                    d={brandConfig.logo.icon.rightBracket}
                    stroke={primaryColor}
                    strokeWidth={brandConfig.logo.strokeWidths.brackets}
                    strokeLinecap="round"
                />
                {/* Checkmark */}
                <path
                    d={brandConfig.logo.icon.checkmark}
                    stroke={accentColor}
                    strokeWidth={brandConfig.logo.strokeWidths.checkmark}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
                {/* Dashed Circle */}
                <circle
                    cx={brandConfig.logo.icon.dashedCircle.cx}
                    cy={brandConfig.logo.icon.dashedCircle.cy}
                    r={brandConfig.logo.icon.dashedCircle.r}
                    stroke={primaryColor}
                    strokeWidth={brandConfig.logo.strokeWidths.dashedCircle}
                    strokeDasharray="4 4"
                    opacity={circleOpacity}
                />
            </svg>
        );
    }

    return (
        <svg
            width={width}
            height={height}
            viewBox="0 0 320 80"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className={className}
        >
            {/* Icon */}
            <path
                d={brandConfig.logo.icon.leftBracket}
                stroke={primaryColor}
                strokeWidth={brandConfig.logo.strokeWidths.brackets}
                strokeLinecap="round"
            />
            <path
                d={brandConfig.logo.icon.rightBracket}
                stroke={primaryColor}
                strokeWidth={brandConfig.logo.strokeWidths.brackets}
                strokeLinecap="round"
            />
            <path
                d={brandConfig.logo.icon.checkmark}
                stroke={accentColor}
                strokeWidth={brandConfig.logo.strokeWidths.checkmark}
                strokeLinecap="round"
                strokeLinejoin="round"
            />
            <circle
                cx={brandConfig.logo.icon.dashedCircle.cx}
                cy={brandConfig.logo.icon.dashedCircle.cy}
                r={brandConfig.logo.icon.dashedCircle.r}
                stroke={primaryColor}
                strokeWidth={brandConfig.logo.strokeWidths.dashedCircle}
                strokeDasharray="4 4"
                opacity={circleOpacity}
            />

            {/* Typography */}
            <text
                x="85"
                y="38"
                fontFamily={brandConfig.fonts.primary}
                fontWeight={brandConfig.fonts.weights.extrabold}
                fontSize={fontSize}
                fill={primaryColor}
            >
                FACTURA
            </text>
            <text
                x="214"
                y="38"
                fontFamily={brandConfig.fonts.primary}
                fontWeight={brandConfig.fonts.weights.light}
                fontSize={fontSize}
                fill={primaryColor}
            >
                SV
            </text>
            <text
                x="85"
                y="62"
                fontFamily={brandConfig.fonts.primary}
                fontWeight={brandConfig.fonts.weights.semibold}
                fontSize={taglineSize}
                fill={accentColor}
                letterSpacing="1.5"
            >
                {brandConfig.tagline}
            </text>
        </svg>
    );
}
